import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronRight,
  Hammer,
  Cpu,
  Shirt,
  Home,
  Car,
  FlaskConical,
  Dumbbell,
  Package,
} from "lucide-react";
import { Button } from "@b2b/ui-components";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@b2b/ui-components";

const categories = [
  {
    id: "machinery",
    name: "Machinery & Equipment",
    icon: Hammer,
    subcategories: [
      { id: "industrial-tools", name: "Industrial Tools" },
      { id: "construction-equipment", name: "Construction Equipment" },
      { id: "agricultural-machinery", name: "Agricultural Machinery" },
      { id: "food-processing", name: "Food Processing Equipment" },
      { id: "packaging-machines", name: "Packaging Machines" },
    ],
  },
  {
    id: "electronics",
    name: "Electronics & Technology",
    icon: Cpu,
    subcategories: [
      { id: "consumer-electronics", name: "Consumer Electronics" },
      { id: "electronic-components", name: "Electronic Components" },
      { id: "telecommunication", name: "Telecommunication Equipment" },
      { id: "computer-hardware", name: "Computer Hardware" },
      { id: "security-systems", name: "Security Systems" },
    ],
  },
  {
    id: "apparel",
    name: "Apparel & Fashion",
    icon: Shirt,
    subcategories: [
      { id: "mens-clothing", name: "Men's Clothing" },
      { id: "womens-fashion", name: "Women's Fashion" },
      { id: "childrens-apparel", name: "Children's Apparel" },
      { id: "fashion-accessories", name: "Fashion Accessories" },
      { id: "footwear", name: "Footwear" },
    ],
  },
  {
    id: "home-garden",
    name: "Home & Garden",
    icon: Home,
    subcategories: [
      { id: "furniture", name: "Furniture" },
      { id: "home-decor", name: "Home Decor" },
      { id: "garden-tools", name: "Garden Tools" },
      { id: "outdoor-equipment", name: "Outdoor Equipment" },
      { id: "kitchen-appliances", name: "Kitchen Appliances" },
    ],
  },
  {
    id: "automotive",
    name: "Automotive & Parts",
    icon: Car,
    subcategories: [
      { id: "auto-parts", name: "Auto Parts" },
      { id: "car-accessories", name: "Car Accessories" },
      { id: "motorcycle-parts", name: "Motorcycle Parts" },
      { id: "commercial-vehicles", name: "Commercial Vehicles" },
      { id: "automotive-tools", name: "Automotive Tools" },
    ],
  },
  {
    id: "chemicals",
    name: "Chemicals & Materials",
    icon: FlaskConical,
    subcategories: [
      { id: "industrial-chemicals", name: "Industrial Chemicals" },
      { id: "specialty-materials", name: "Specialty Materials" },
      { id: "cleaning-chemicals", name: "Cleaning Chemicals" },
      { id: "construction-chemicals", name: "Construction Chemicals" },
      { id: "laboratory-chemicals", name: "Laboratory Chemicals" },
    ],
  },
  {
    id: "sports",
    name: "Sports & Recreation",
    icon: Dumbbell,
    subcategories: [
      { id: "fitness-equipment", name: "Fitness Equipment" },
      { id: "outdoor-sports", name: "Outdoor Sports" },
      { id: "team-sports", name: "Team Sports" },
      { id: "water-sports", name: "Water Sports" },
      { id: "sports-accessories", name: "Sports Accessories" },
    ],
  },
  {
    id: "beauty",
    name: "Beauty & Personal Care",
    icon: Package,
    subcategories: [
      { id: "skincare", name: "Skincare" },
      { id: "haircare", name: "Haircare" },
      { id: "makeup", name: "Makeup" },
      { id: "fragrances", name: "Fragrances" },
      { id: "personal-hygiene", name: "Personal Hygiene" },
    ],
  },
  {
    id: "food",
    name: "Food & Beverages",
    icon: Package,
    subcategories: [
      { id: "packaged-foods", name: "Packaged Foods" },
      { id: "snacks", name: "Snacks" },
      { id: "beverages", name: "Beverages" },
      { id: "frozen-foods", name: "Frozen Foods" },
      { id: "organic-products", name: "Organic Products" },
    ],
  },
  {
    id: "health",
    name: "Healthcare & Medical",
    icon: Package,
    subcategories: [
      { id: "medical-devices", name: "Medical Devices" },
      { id: "pharmaceuticals", name: "Pharmaceuticals" },
      { id: "surgical-supplies", name: "Surgical Supplies" },
      { id: "hospital-equipment", name: "Hospital Equipment" },
      { id: "diagnostic-tools", name: "Diagnostic Tools" },
    ],
  },
  {
    id: "office-supplies",
    name: "Office Supplies & Stationery",
    icon: Package,
    subcategories: [
      { id: "writing-instruments", name: "Writing Instruments" },
      { id: "notebooks", name: "Notebooks & Paper" },
      { id: "office-furniture", name: "Office Furniture" },
      { id: "printing-supplies", name: "Printing Supplies" },
      { id: "filing-products", name: "Filing Products" },
    ],
  },
  {
    id: "toys",
    name: "Toys & Games",
    icon: Package,
    subcategories: [
      { id: "educational-toys", name: "Educational Toys" },
      { id: "puzzles", name: "Puzzles & Board Games" },
      { id: "outdoor-toys", name: "Outdoor Toys" },
      { id: "plush-toys", name: "Plush Toys" },
      { id: "remote-control", name: "Remote Control Toys" },
    ],
  },
  {
    id: "books",
    name: "Books & Media",
    icon: Package,
    subcategories: [
      { id: "fiction", name: "Fiction" },
      { id: "non-fiction", name: "Non-Fiction" },
      { id: "educational", name: "Educational" },
      { id: "magazines", name: "Magazines" },
      { id: "ebooks", name: "eBooks" },
    ],
  },
  {
    id: "jewelry",
    name: "Jewelry & Accessories",
    icon: Package,
    subcategories: [
      { id: "necklaces", name: "Necklaces" },
      { id: "rings", name: "Rings" },
      { id: "bracelets", name: "Bracelets" },
      { id: "earrings", name: "Earrings" },
      { id: "watches", name: "Watches" },
    ],
  },
  {
    id: "travel",
    name: "Travel & Luggage",
    icon: Package,
    subcategories: [
      { id: "suitcases", name: "Suitcases" },
      { id: "backpacks", name: "Backpacks" },
      { id: "handbags", name: "Handbags" },
      { id: "travel-accessories", name: "Travel Accessories" },
      { id: "duffle-bags", name: "Duffle Bags" },
    ],
  },
  {
    id: "pets",
    name: "Pet Supplies",
    icon: Package,
    subcategories: [
      { id: "dog-supplies", name: "Dog Supplies" },
      { id: "cat-supplies", name: "Cat Supplies" },
      { id: "aquarium", name: "Aquarium & Fish" },
      { id: "bird-supplies", name: "Bird Supplies" },
      { id: "pet-toys", name: "Pet Toys" },
    ],
  },
  {
    id: "energy",
    name: "Energy & Utilities",
    icon: Package,
    subcategories: [
      { id: "solar-products", name: "Solar Products" },
      { id: "batteries", name: "Batteries & Storage" },
      { id: "wind-energy", name: "Wind Energy Equipment" },
      { id: "power-generators", name: "Power Generators" },
      { id: "electric-vehicles", name: "Electric Vehicles" },
    ],
  },
  {
    id: "construction",
    name: "Construction & Real Estate",
    icon: Package,
    subcategories: [
      { id: "building-materials", name: "Building Materials" },
      { id: "cement", name: "Cement & Concrete" },
      { id: "steel", name: "Steel & Structures" },
      { id: "real-estate", name: "Real Estate Projects" },
      { id: "architecture", name: "Architecture Services" },
    ],
  },
  {
    id: "education",
    name: "Education & Training",
    icon: Package,
    subcategories: [
      { id: "online-courses", name: "Online Courses" },
      { id: "coaching", name: "Coaching Classes" },
      { id: "certifications", name: "Certifications" },
      { id: "books", name: "Books & Study Material" },
      { id: "language-training", name: "Language Training" },
    ],
  },
];

interface CategorySidebarProps {
  className?: string;
  categories?: Array<{
    id?: string;
    slug?: string;
    name: string;
    children?: Array<{
      id?: string;
      slug?: string;
      name: string;
      children?: Array<{ id?: string; slug?: string; name: string }>;
    }>;
  }>;
  onSelectLevel0?: (categoryId: string) => void;
  onSelectLevel1?: (subcategoryId: string) => void;
  selectedLevel0Id?: string | null;
  selectedLevel1Id?: string | null;
}

export const CategorySidebar = ({
  className = "",
  categories: dynamicCategories,
  onSelectLevel0,
  onSelectLevel1,
  selectedLevel0Id,
  selectedLevel1Id,
}: CategorySidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedLevel1, setExpandedLevel1] = useState<Set<string>>(new Set());
  // Local accordion state for smooth collapse - syncs with parent but allows immediate UI updates
  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    selectedLevel0Id || undefined
  );
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (id: string) =>
    id ? location.pathname.includes(id) : false;

  const toggleLevel1 = (id: string) => {
    setExpandedLevel1((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sync accordion value with parent selection when parent explicitly changes
  useEffect(() => {
    if (selectedLevel0Id && selectedLevel0Id !== accordionValue) {
      // Parent selected a category - sync immediately
      setAccordionValue(selectedLevel0Id);
    }
    // Note: We don't sync when parent clears (selectedLevel0Id becomes null)
    // because the accordion handles collapse via onValueChange
  }, [selectedLevel0Id]);

  // Track previous Level 0 to detect changes
  const prevLevel0Ref = useRef<string | null>(null);

  // Clear Level 1 expansion when Level 0 changes to a different category
  useEffect(() => {
    if (
      prevLevel0Ref.current !== null &&
      prevLevel0Ref.current !== selectedLevel0Id
    ) {
      // Level 0 changed - clear all Level 1 expansions
      setExpandedLevel1(new Set());
    }
    prevLevel0Ref.current = selectedLevel0Id || null;
  }, [selectedLevel0Id]);

  // Auto-expand Level 1 when selected from parent
  useEffect(() => {
    if (selectedLevel1Id) {
      setExpandedLevel1((prev) => {
        const next = new Set(prev);
        next.add(selectedLevel1Id);
        return next;
      });
    } else if (selectedLevel0Id) {
      // Clear expansion if Level 1 is deselected (but keep Level 1s from current Level 0)
      // Only clear if we have a Level 0 selected
      setExpandedLevel1(new Set());
    }
  }, [selectedLevel1Id, selectedLevel0Id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);

  const sidebarCategories =
    dynamicCategories && dynamicCategories.length > 0
      ? dynamicCategories.map((c) => ({
          id: c.slug || String(c.id ?? ""),
          name: c.name,
          icon: Package,
          children: c.children,
        }))
      : [];

  return (
    <div className={`bg-card border-r border-border ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-2 mb-2">
          <Package className="h-5 w-5 text-orange-500" />
          <h2 className="text-lg font-semibold text-foreground">Categories</h2>
        </div>
        <p className="text-sm text-muted-foreground">Browse by category</p>
      </div>
      <div className="p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <Accordion
          type="single"
          collapsible
          className="space-y-2"
          value={accordionValue}
          onValueChange={(newValue) => {
            // Clear any pending collapse timeout
            if (collapseTimeoutRef.current) {
              clearTimeout(collapseTimeoutRef.current);
              collapseTimeoutRef.current = null;
            }

            // Update local accordion state immediately for smooth UI
            setAccordionValue(newValue);

            if (newValue) {
              // Opening/selecting a category - update parent immediately
              onSelectLevel0?.(newValue);
            } else {
              // User is collapsing - delay parent update to allow animation
              const currentValue = selectedLevel0Id;
              if (currentValue) {
                collapseTimeoutRef.current = setTimeout(() => {
                  onSelectLevel0?.("");
                  collapseTimeoutRef.current = null;
                }, 250); // Allow collapse animation to complete
              }
            }
          }}
        >
          {sidebarCategories.map((category) => (
            <AccordionItem
              key={category.id}
              value={category.id}
              className="border border-border rounded-lg overflow-hidden"
            >
              <AccordionTrigger
                className={`px-4 py-3 hover:no-underline hover:bg-muted/50 transition-colors ${
                  selectedLevel0Id === category.id
                    ? "bg-orange-100 text-orange-600"
                    : "text-foreground"
                }`}
              >
                <div className="flex items-center gap-3">
                  <category.icon className="h-4 w-4" />
                  <span className="text-left font-medium">{category.name}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <div className="bg-muted/30">
                  {(category.children || []).map((subcategory) => {
                    const hasLevel2 =
                      Array.isArray(subcategory.children) &&
                      subcategory.children.length > 0;
                    const subId =
                      (subcategory as any).slug || String(subcategory.id ?? "");
                    return (
                      <div key={subcategory.id} className="py-1">
                        <Button
                          variant="ghost"
                          className={`w-full justify-start px-6 py-2 h-auto text-sm rounded-none hover:bg-muted/60 ${
                            selectedLevel1Id === subId ||
                            expandedLevel1.has(subId)
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground"
                          } flex items-center`}
                          onClick={() => {
                            if (hasLevel2) {
                              toggleLevel1(subId);
                            } else {
                              const leaf = subcategory as any;
                              navigate(`/search/${leaf.slug ?? leaf.id ?? ""}`);
                            }
                            // Notify parent to show Level 2 list on the right side when L1 is clicked
                            onSelectLevel1?.(subId);
                          }}
                          type="button"
                        >
                          <ChevronRight
                            className={`h-3 w-3 mr-2 transition-transform ${hasLevel2 && expandedLevel1.has(subId) ? "rotate-90" : ""}`}
                          />
                          {subcategory.name}
                          {hasLevel2 && (
                            <span className="ml-auto">
                              {/* Caret for expandable Level 1 */}
                              <svg
                                className={`inline h-3 w-3 transition-transform ${expandedLevel1.has(subId) ? "rotate-90" : ""}`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M6 8l4 4 4-4"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          )}
                        </Button>
                        {/* Render Level 2 categories */}
                        {hasLevel2 && expandedLevel1.has(subId) && (
                          <div className="ml-8 border-l border-muted py-1">
                            {(subcategory.children || []).map((subchild) => {
                              const subChildId = String(
                                (subchild as any).id ?? ""
                              );
                              const subChildSlug = (subchild as any).slug as
                                | string
                                | undefined;
                              return (
                                <Button
                                  key={subChildId}
                                  variant="ghost"
                                  className={`w-full justify-start px-4 py-2 h-auto text-xs rounded-none hover:bg-muted/60 ${
                                    isActive(subChildId)
                                      ? "bg-primary/10 text-primary"
                                      : "text-muted-foreground"
                                  }`}
                                  onClick={() =>
                                    navigate(
                                      `/search/${subChildSlug ?? subChildId}`
                                    )
                                  }
                                >
                                  <ChevronRight className="h-3 w-3 mr-1.5" />
                                  {(subchild as any).name}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};
